import { useEffect, useRef, useCallback } from "react";
import { useAtom } from "jotai";
import {
  productsAtom,
  productsByIdAtom,
  productsByUrlAtom,
  inventoryLocationsAtom,
  productRelationsAtom,
  webSocketConnectedAtom,
} from "../state.js";

const RECONNECT_DELAY = 5000;

const getWebSocketURL = () => {
  const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${protocol}//${window.location.host}/ws`;
};

function useWebSocket() {
  const wsRef = useRef(null);
  const reconnectRef = useRef(null);
  const shouldReconnectRef = useRef(false);
  const connectionIdRef = useRef(0);
  const messageHandlerRef = useRef(null);
  const [isConnected, setConnected] = useAtom(webSocketConnectedAtom);

  const [, updateProducts] = useAtom(productsAtom);
  const [productsById, updateProductsById] = useAtom(productsByIdAtom);
  const [, updateProductsByUrl] = useAtom(productsByUrlAtom);
  const [, setInventoryLocations] = useAtom(inventoryLocationsAtom);
  const [, setProductRelations] = useAtom(productRelationsAtom);
  const productsByIdRef = useRef(productsById);

  useEffect(() => {
    productsByIdRef.current = productsById;
  }, [productsById]);

  const clearReconnect = useCallback(() => {
    if (reconnectRef.current) {
      clearTimeout(reconnectRef.current);
      reconnectRef.current = null;
    }
  }, []);

  const handleMessage = useCallback(
    (event, ws) => {
      let data;
      try {
        data = JSON.parse(event.data);
      } catch (error) {
        console.error("Error parsing JSON:", event.data);
        return;
      }

      if (data.ping) {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ pong: true }));
        }
        return;
      }

      console.log(data);
      switch (data.action) {
        case "create":
          switch (data.type) {
            case "inventory_location":
              const inventoryLocation = data.data;
              if (inventoryLocation) {
                setInventoryLocations((currentLocations) => ({
                  ...currentLocations,
                  [inventoryLocation.id]: inventoryLocation,
                }));
              }
              break;
            case "product":
              const product = data.data;
              if (product && product.url && product.part_number) {
                updateProducts({ [product.url]: product });
                updateProductsById({ [product.id]: product });
                productsByIdRef.current = { ...productsByIdRef.current, [product.id]: product };
                updateProductsByUrl({ [product.url]: product.id });
              } else {
                console.error("Product has no url:", product);
              }
              break;
            case "product_relation":
              const relation = data.data;
              if (relation) {
                setProductRelations((currentRelations) => {
                  if (Array.isArray(currentRelations)) {
                    return [relation, ...currentRelations];
                  }

                  console.error("productRelations is not an array");
                  return currentRelations;
                });
              }
              break;
            default:
              console.log("Unhandled create type:", data.type);
              break;
          }
          break;
        case "delete":
          switch (data.type) {
            case "inventory_location":
              const inventoryLocation = data.data;
              if (inventoryLocation) {
                setInventoryLocations((currentLocations) => {
                  const remainingLocations = { ...currentLocations };
                  delete remainingLocations[inventoryLocation.id];
                  return remainingLocations;
                });
              }
              break;
            case "product_relation":
              const relation = data.data;
              if (relation) {
                setProductRelations((currentRelations) => {
                  if (Array.isArray(currentRelations)) {
                    return currentRelations.filter((r) => r.id !== relation.id);
                  }

                  console.error("productRelations is not an array");
                  return currentRelations;
                });
              }
              break;
            default:
              console.log("Unhandled delete type:", data.type);
              break;
          }
          break;
        case "update":
          switch (data.type) {
            case "brand":
              break;
            case "inventory_location":
              const inventoryLocation = data.data;
              if (inventoryLocation) {
                setInventoryLocations((currentLocations) => ({
                  ...currentLocations,
                  [inventoryLocation.id]: inventoryLocation,
                }));
              }
              break;
            case "product":
              const product = data.data;
              if (product && product.url && product.part_number) {
                const oldProduct = productsByIdRef.current[product.id];
                if (!oldProduct) {
                  console.error("Product not found:", product.id);
                  break;
                }
                updateProducts({ [product.url]: product });
                updateProductsById({ [product.id]: product });
                productsByIdRef.current = { ...productsByIdRef.current, [product.id]: product };
                if (oldProduct.url !== product.url) {
                  updateProductsByUrl({ [oldProduct.url]: null });
                }
                updateProductsByUrl({ [product.url]: product.id });
              } else {
                console.error("Product has no url:", product);
              }
              break;
            case "product_group":
              break;
            default:
              console.log("Unhandled type:", data.type);
              break;
          }
          break;
        default:
          console.log(
            "Unhandled action:",
            data.action,
            ", type:",
            data.type,
            ", part number:",
            data.data?.part_number,
          );
          break;
      }
    },
    [setInventoryLocations, setProductRelations, updateProducts, updateProductsById, updateProductsByUrl],
  );

  useEffect(() => {
    messageHandlerRef.current = handleMessage;
  }, [handleMessage]);

  const setupWebSocket = useCallback(() => {
    clearReconnect();

    if (wsRef.current) {
      wsRef.current.onopen = null;
      wsRef.current.onmessage = null;
      wsRef.current.onerror = null;
      wsRef.current.onclose = null;

      if (wsRef.current.readyState === WebSocket.CONNECTING || wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.close();
      }
    }

    const connectionId = connectionIdRef.current + 1;
    connectionIdRef.current = connectionId;
    const ws = new WebSocket(getWebSocketURL(), "ecup");
    wsRef.current = ws;

    const isCurrentSocket = () => wsRef.current === ws && connectionIdRef.current === connectionId;

    const scheduleReconnect = () => {
      if (!shouldReconnectRef.current || !isCurrentSocket() || reconnectRef.current) {
        return;
      }

      reconnectRef.current = setTimeout(() => {
        reconnectRef.current = null;
        setupWebSocket();
      }, RECONNECT_DELAY);
    };

    ws.onopen = () => {
      if (!isCurrentSocket()) {
        return;
      }

      clearReconnect();
      setConnected(true);
    };

    ws.onerror = (errorEvent) => {
      if (!isCurrentSocket()) {
        return;
      }

      console.error("WebSocket error:", errorEvent);
      setConnected(false);
      scheduleReconnect();
    };

    ws.onclose = (event) => {
      if (!isCurrentSocket()) {
        return;
      }

      console.log("WebSocket closed:", event);
      setConnected(false);
      scheduleReconnect();
    };

    ws.onmessage = (event) => {
      if (!isCurrentSocket()) {
        return;
      }

      messageHandlerRef.current?.(event, ws);
    };
  }, [clearReconnect, setConnected]);

  useEffect(() => {
    shouldReconnectRef.current = true;
    setupWebSocket();

    return () => {
      shouldReconnectRef.current = false;
      clearReconnect();
      if (wsRef.current) {
        wsRef.current.onopen = null;
        wsRef.current.onmessage = null;
        wsRef.current.onerror = null;
        wsRef.current.onclose = null;
        wsRef.current.close();
      }
      setConnected(false);
    };
  }, [clearReconnect, setConnected, setupWebSocket]);

  const sendMessage = (message) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
    }
  };

  return {
    sendMessage,
    isConnected,
  };
}

export default useWebSocket;
